import clsx from 'clsx';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { TEAM_OPTIONS } from '@cms/utils/constants';
import { mapLookupsToOptions } from '@cms/utils/helpers';
import { collectionsLookupsQuery, rolesLookupsQuery } from '@cms/utils/queries';
import { useAppSelector } from '@cms/utils/store/hooks';
import { ISystemUser } from '@dl/declarations';
import { guestInitialValues, guestSchema, GuestUserFormData, UserFormData, userInitialValues, userSchema } from '../../utils/forms/user';
import { UsersPath } from '../../utils/routes';
import { SystemCheckbox } from '../data/Checkbox';
import { ControlledDropdown } from '../data/Dropdown';
import { ControlledAssetInput } from '../data/FileInput';
import { ControlledSystemTextInput } from '../data/TextInput';
import { FormContainer } from './FormContainer';

import { BsPersonSquare } from 'react-icons/bs';

interface FormProps extends Pick<React.ComponentProps<typeof FormContainer>,
    "formOptions"
    | "headerOptions"
    | "handleCancelOverride"
    | "handleCreateRecordOverride"
    | "handleUpdateRecordOverride"
    | "handlePostSubmit"
> { }

export const UserForm: React.FC<FormProps> = (props) => {
    return (
        <FormContainer<UserFormData>
            title='User'
            headerOptions={{
                show: false
            }}
            queryOptions={{
                enabled: true,
                basePath: UsersPath
            }}
            schema={userSchema}
            defaultValues={userInitialValues}
            formSize="1/3"
            {...props}
        >
            <Form type='User' />
        </FormContainer>
    )
}

export const GuestForm: React.FC<FormProps> = (props) => {
    return (
        <FormContainer<GuestUserFormData>
            title='User'
            headerOptions={{
                show: false
            }}
            queryOptions={{
                enabled: true,
                basePath: UsersPath
            }}
            schema={guestSchema}
            defaultValues={guestInitialValues}
            shouldRedirectAfterSubmit={false}
            formSize="2/3"
            {...props}
        >
            <Form type='Guest' />
        </FormContainer>
    )
}

const Form = ({ type }: { type: "User" | "Guest" }) => {
    const { watch, setValue } = useFormContext<UserFormData | GuestUserFormData>();
    const { entity } = useAppSelector((state) => state.form);

    const { data: roles } = rolesLookupsQuery({}, { fetchOnce: true });

    const { data: collections = [] } = collectionsLookupsQuery({
        collection_type: 'User'
    })

    const isActive = watch('is_active', false);

    useEffect(() => {
        setValue('collections', ((entity as ISystemUser)?.collectionsObjects || []).map((item) => item.collection_ref))
    }, [entity])

    return (
        <div className='flex flex-col items-center md:grid md:grid-cols-2 md:items-start gap-4 w-full text-[12px]'>
            <div className={clsx('col-span-full gap-8', type === "User" && "grid grid-cols-3")}>
                <div className='col-span-2 space-y-4'>
                    {type === "User" && (
                        <ControlledDropdown
                            inputSize='full'
                            name='team'
                            options={TEAM_OPTIONS}
                            valueKey='slug'
                            allowSingleDeselect={false}
                            searchOptions={{
                                allow: false
                            }}
                        />
                    )}

                    <ControlledSystemTextInput
                        inputSize='full'
                        name='first_name'
                        description='First name'
                        required
                    />

                    <ControlledSystemTextInput
                        inputSize='full'
                        name='last_name'
                        description='Last name'
                        required
                    />
                </div>

                {type === "User" && (
                    <ControlledAssetInput
                        name="image"
                        mainAction={{
                            show: true,
                            actionText: "Update image",
                            actionIcon: null,
                            mainIcon: <BsPersonSquare className="w-6 h-6" />
                        }}
                        initialValue={(entity as ISystemUser)?.imageObject}
                        showFullPreview={false}
                    />
                )}
            </div>

            {type === "User" && (
                <>
                    <ControlledDropdown
                        description='Collection'
                        name='collections'
                        options={mapLookupsToOptions(collections, 'name', () => 'Collections')}
                        label='Add to collections'
                        valueKey='_id'
                        inputSize='full'
                        searchOptions={{
                            allow: false
                        }}
                        allowMultiSelect
                    />

                    <div className='col-span-full'>
                        <ControlledSystemTextInput
                            inputSize='full'
                            name='email'
                            description='Email'
                            required
                        />
                    </div>

                    <div className='col-span-full'>
                        <ControlledSystemTextInput
                            inputSize='full'
                            name='title'
                            description={`User's role`}
                            required
                        />
                    </div>

                    <div
                        className='col-span-full'
                    >
                        <ControlledDropdown
                            inputSize='full'
                            name='custom_role'
                            options={mapLookupsToOptions(roles || [], 'name', () => 'role')}
                            valueKey='_id'
                            description='Permissions'
                            searchOptions={{
                                allow: false
                            }}
                        />
                    </div>

                    <div className='col-span-full'>
                        <ControlledSystemTextInput
                            inputSize='full'
                            name='long_bio'
                            description='Bio'
                            rows={5}
                            required
                            multiline
                        />
                    </div>

                    <ControlledSystemTextInput
                        inputSize='full'
                        name='social_links.facebook'
                        description='Facebook link'
                    />

                    <ControlledSystemTextInput
                        inputSize='full'
                        name='social_links.linkedin'
                        description='LinkedIn link'
                    />

                    <ControlledSystemTextInput
                        inputSize='full'
                        name='social_links.rss'
                        description='RSS link'
                    />

                    <ControlledSystemTextInput
                        inputSize='full'
                        name='social_links.twitter'
                        description='Twitter link'
                    />

                    <ControlledSystemTextInput
                        inputSize='full'
                        name='social_links.telegram'
                        description='Telegram link'
                    />

                    <ControlledSystemTextInput
                        inputSize='full'
                        name='social_links.reddit'
                        description='Reddit link'
                    />
                </>
            )}

            <div className="col-span-full font-medium">
                * Mandatory fields
            </div>

            {type === "User" && (
                <div
                    className='col-span-full flex items-center justify-end'
                >
                    <SystemCheckbox
                        handleChange={(checked) => {
                            setValue('is_active', checked);
                        }}
                        label='Active'
                        checked={isActive}
                    />
                </div>
            )}
        </div>
    )
}